import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { PinInput } from "@/components/PinInput";
import { AuthCard, AuthMessage, authInputCls, authButtonCls, authLabelCls } from "./components/AuthCard";

const linkCls = "inline-block py-1.5 text-sm font-semibold text-navy underline decoration-gold/60 underline-offset-4 hover:text-gold transition-colors";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pinEnabled, setPinEnabled] = useState(null);
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(location.state?.message || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => navigate("/admin", { replace: true })).catch(() => {});
    api.get("/auth/options")
      .then((r) => { setPinEnabled(Boolean(r.data.pin_enabled)); if (r.data.pin_enabled) setMode("pin"); })
      .catch(() => setPinEnabled(false));
  }, [navigate]);

  const run = async (request) => {
    setError("");
    setNotice("");
    setLoading(true);
    try {
      await request();
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = (e) => { e.preventDefault(); run(() => api.post("/auth/login", { email, password })); };
  const submitPin = (e) => {
    e.preventDefault();
    if (pin.length !== 7) { setError("PIN must be exactly 7 digits."); return; }
    run(() => api.post("/auth/login-pin", { pin }));
  };
  const switchMode = (next) => { setMode(next); setError(""); setPin(""); };

  const forgot = <Link to="/admin/forgot-password" data-testid="forgot-password-link" className={linkCls}>Forgot PIN or password?</Link>;
  const messages = (
    <>
      {notice && <AuthMessage tone="success" testid="admin-login-notice">{notice}</AuthMessage>}
      {error && <AuthMessage testid="admin-login-error">{error}</AuthMessage>}
    </>
  );

  if (mode === "pin") {
    return (
      <AuthCard testid="admin-login-card">
        <form onSubmit={submitPin} className="space-y-5" data-testid="admin-pin-form">
          <div>
            <label className={authLabelCls} htmlFor="a-pin">Admin PIN</label>
            <PinInput id="a-pin" testid="admin-pin-input" className={authInputCls} value={pin} onChange={setPin} autoFocus />
            <p className="mt-1.5 text-xs text-slate-500">Enter your 7-digit PIN.</p>
          </div>
          {messages}
          <button type="submit" disabled={loading} data-testid="admin-pin-login-button" className={authButtonCls}>
            {loading ? "SIGNING IN…" : "SIGN IN"}
          </button>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button type="button" onClick={() => switchMode("password")} data-testid="use-password-link" className={linkCls}>Use password instead</button>
            {forgot}
          </div>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard testid="admin-login-card">
      <form onSubmit={submitPassword} className="space-y-5" data-testid="admin-login-form">
        <div>
          <label className={authLabelCls} htmlFor="a-email">Email</label>
          <input id="a-email" type="email" required data-testid="admin-email-input" className={authInputCls} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
        </div>
        <div>
          <label className={authLabelCls} htmlFor="a-pass">Password</label>
          <PasswordInput id="a-pass" testid="admin-password-input" className={authInputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          <div className="mt-2 text-right">{forgot}</div>
        </div>
        {messages}
        <button type="submit" disabled={loading} data-testid="admin-login-button" className={authButtonCls}>
          {loading ? "SIGNING IN…" : "SIGN IN"}
        </button>
        {pinEnabled && (
          <button type="button" onClick={() => switchMode("pin")} data-testid="use-pin-link" className={linkCls}>Use PIN instead</button>
        )}
      </form>
    </AuthCard>
  );
}
